import { useState, useEffect } from 'react'
import { X, ArrowRight, ArrowLeft, Check, Users, Calendar, Building2 } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useSeason, formatSeasonLong } from '../context/SeasonContext'
import { useCategoriesOptions } from '../constants/categories'
import api from '../services/api'
import { T } from '../theme'
import LevelSelector, { categoryHasLevels } from './LevelSelector'

/**
 * SeasonAssistant — assistant 2 étapes pour préparer la saison à venir.
 *
 * Étape 1 — Mon club & ma catégorie
 *   - Plan Free/Coach+ : club libre (changement possible)
 *   - Plan Club : club bloqué sur le club Insightball actuel
 *
 * Étape 2 — Mon effectif
 *   - Repartir de zéro (effectif vide)
 *   - Reprendre les joueurs de la saison précédente (sélection + ajustement catégorie)
 *
 * Principe : simple et intuitif. Chaque étape est skippable. Tout est modifiable après.
 */

// La liste des catégories vient désormais du hook SSoT (BUG-012, ARCHI-002).
// Vétérans/Féminines/U13-U9 disparaissent : Insightball ne supporte plus
// que U14..Seniors (décision Tchitcha 24 mai). Pour les coachs déjà sur
// ces valeurs, dropdown vide → ils choisissent une catégorie SSoT.

export default function SeasonAssistant({ isOpen, onClose, targetSeason }) {
  const { user } = useAuth()
  const { categories } = useCategoriesOptions()
  const { currentSeason, reloadSeasonStatus, setSelectedSeason } = useSeason()

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Détection du plan : Club = immuable, Free/Coach+ = libre
  const planLocked = user?.plan === 'CLUB' || user?.plan === 'CLUB_PRO'

  // Étape 1 — Club + catégorie
  const [clubName, setClubName] = useState('')
  const [category, setCategory] = useState('Seniors')
  const [level, setLevel] = useState(null)

  // Étape 2 — Effectif
  const [rosterChoice, setRosterChoice] = useState(null) // 'fresh' | 'copy' | null
  const [previousPlayers, setPreviousPlayers] = useState([])
  const [selectedPlayerIds, setSelectedPlayerIds] = useState(new Set())
  const [categoryAdjustments, setCategoryAdjustments] = useState({}) // { player_id: new_category }
  const [bulkPromote, setBulkPromote] = useState(false)

  // Init quand l'assistant s'ouvre
  useEffect(() => {
    if (!isOpen) return
    setStep(1)
    setError(null)

    // Préremplir avec les valeurs actuelles
    setClubName(user?.club_name || user?.club?.name || '')
    // BUG-012-bis : init vide. Un useEffect dédié applique user.team_category
    // comme smart default une fois la SSoT chargée (cf. pattern PlayerForm).
    setCategory('')
    setLevel(null)
    setRosterChoice(null)
    setSelectedPlayerIds(new Set())
    setCategoryAdjustments({})
    setBulkPromote(false)

    // Charger les joueurs de la saison précédente
    api.get('/players/', { params: { season: currentSeason } })
      .catch(() => ({ data: [] }))
      .then(playersRes => {
        const players = Array.isArray(playersRes.data) ? playersRes.data : []
        setPreviousPlayers(players)
        setSelectedPlayerIds(new Set(players.map(p => p.id))) // tous cochés par défaut
      })
  }, [isOpen, currentSeason, user])

  // Smart default catégorie (BUG-012-bis) : pré-sélectionne user.team_category
  // dès que la liste SSoT est chargée, si elle y est. Sinon vide. Effet séparé
  // de la reset pour ne pas écraser un choix utilisateur en cours.
  useEffect(() => {
    if (!isOpen) return
    if (category) return
    if (!user?.team_category) return
    if (!categories.includes(user.team_category)) return
    setCategory(user.team_category)
  }, [isOpen, categories, user, category])

  if (!isOpen) return null

  // ── Gestion étape 1 ──
  const needsLevel = categoryHasLevels(category)
  const canProceedStep1 = clubName.trim().length > 0 && category && (!needsLevel || !!level)

  // ── Gestion étape 2 ──
  const togglePlayer = (id) => {
    const next = new Set(selectedPlayerIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedPlayerIds(next)
  }

  const adjustCategory = (id, newCat) => {
    setCategoryAdjustments({ ...categoryAdjustments, [id]: newCat })
  }

  // Promotion automatique : U17 → U18, U18 → Seniors, etc.
  const applyBulkPromote = () => {
    const promotion = {
      'U14': 'U15', 'U15': 'U16', 'U16': 'U17', 'U17': 'U18', 'U18': 'Seniors',
      'U13': 'U14', 'U12': 'U13', 'U11': 'U12', 'U10': 'U11', 'U9': 'U10',
    }
    const adj = {}
    previousPlayers.forEach(p => {
      if (selectedPlayerIds.has(p.id) && promotion[p.category]) {
        adj[p.id] = promotion[p.category]
      }
    })
    setCategoryAdjustments(adj)
    setBulkPromote(true)
  }

  const resetBulk = () => {
    setCategoryAdjustments({})
    setBulkPromote(false)
  }

  // ── Submission finale ──
  const finish = async () => {
    setLoading(true)
    setError(null)
    try {
      // 1. Créer/modifier le rattachement coach-club
      await api.post('/coach/club-history/', {
        season: targetSeason,
        club_name: clubName.trim(),
        category: category,
        level: level || null,
      })

      // 2. Copier les joueurs si demandé
      if (rosterChoice === 'copy' && selectedPlayerIds.size > 0) {
        const playersPayload = Array.from(selectedPlayerIds).map(id => ({
          source_player_id: id,
          new_category: categoryAdjustments[id] || null,
        }))
        await api.post('/players/copy-to-season/', {
          source_season: currentSeason,
          target_season: targetSeason,
          players: playersPayload,
          link_to_source: true,
        })
      }

      // 3. Recharger l'état de préparation
      await reloadSeasonStatus()

      // 4. Bascule sur la saison à venir
      setSelectedSeason(targetSeason)

      onClose()

      // Reload pour que toutes les pages se mettent à jour avec la nouvelle saison
      setTimeout(() => window.location.reload(), 100)
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur lors de la préparation. Réessaie.')
    } finally {
      setLoading(false)
    }
  }

  // ── Render ──
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(10,9,8,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: T.bg, maxWidth: 720, width: '100%',
        maxHeight: '92vh', overflowY: 'auto',
        border: `1px solid ${T.rule}`,
        borderTop: `3px solid ${T.gold}`,
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px', borderBottom: `1px solid ${T.rule}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.gold, marginBottom: 6 }}>
              Étape {step}/2 · Préparer {formatSeasonLong(targetSeason)}
            </div>
            <h2 style={{ fontFamily: T.display, fontSize: 26, textTransform: 'uppercase', lineHeight: 1.05, margin: 0, color: T.ink }}>
              {step === 1 && 'Ton club et ta catégorie'}
              {step === 2 && 'Ton effectif'}
            </h2>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: T.muted }}>
            <X size={18} />
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', height: 3, background: T.rule }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{
              flex: 1,
              background: s <= step ? T.gold : 'transparent',
              transition: 'background .25s',
            }} />
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '28px' }}>
          {error && (
            <div style={{ marginBottom: 20, padding: '12px 14px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: '#dc2626', fontFamily: T.mono, fontSize: 11 }}>
              {error}
            </div>
          )}

          {/* ─── ÉTAPE 1 — Club & catégorie ─── */}
          {step === 1 && (
            <div>
              <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.6, marginBottom: 24 }}>
                On va préparer ta saison {targetSeason}. Indique ton club et la catégorie que tu vas entraîner.
              </p>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
                  <Building2 size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
                  Ton club
                  {planLocked && <span style={{ marginLeft: 8, color: T.gold }}>(plan Club, immuable)</span>}
                </label>
                <input
                  type="text"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                  disabled={planLocked}
                  placeholder="Nom du club"
                  style={{
                    width: '100%', padding: '12px 14px',
                    background: planLocked ? '#f5f2eb' : T.surface,
                    border: `1px solid ${T.rule}`,
                    fontSize: 14, color: T.ink,
                    fontFamily: T.body,
                    outline: 'none', boxSizing: 'border-box',
                    cursor: planLocked ? 'not-allowed' : 'text',
                    opacity: planLocked ? 0.7 : 1,
                  }}
                />
                {!planLocked && (
                  <p style={{ fontSize: 11, color: T.muted, marginTop: 6, fontFamily: T.mono, letterSpacing: '.04em' }}>
                    Tu changes de club ? Indique simplement le nouveau nom.
                  </p>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
                  <Users size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
                  Catégorie entraînée
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 6 }}>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setCategory(cat)}
                      style={{
                        padding: '10px 8px',
                        background: category === cat ? T.gold : T.surface,
                        border: `1px solid ${category === cat ? T.gold : T.rule}`,
                        color: category === cat ? T.ink : T.muted,
                        fontFamily: T.mono, fontSize: 10, letterSpacing: '.06em',
                        cursor: 'pointer', fontWeight: category === cat ? 700 : 400,
                        transition: 'all .12s',
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Sélecteur de niveau (Comparatif National) ── */}
              <div style={{ marginTop: 24 }}>
                <LevelSelector
                  category={category}
                  value={level}
                  onChange={setLevel}
                  required={needsLevel}
                  size="normal"
                />
              </div>
            </div>
          )}

          {/* ─── ÉTAPE 2 — Effectif ─── */}
          {step === 2 && (
            <div>
              <p style={{ fontSize: 14, color: T.muted, lineHeight: 1.6, marginBottom: 20 }}>
                Comment veux-tu construire ton effectif {targetSeason} ?
              </p>

              {/* Choix du mode */}
              {rosterChoice === null && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <button
                    onClick={() => setRosterChoice('fresh')}
                    style={{
                      padding: '24px 20px', background: T.surface,
                      border: `1.5px solid ${T.rule}`,
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'border-color .15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = T.gold}
                    onMouseLeave={e => e.currentTarget.style.borderColor = T.rule}
                  >
                    <div style={{ fontFamily: T.display, fontSize: 18, textTransform: 'uppercase', color: T.ink, marginBottom: 8 }}>Repartir de zéro</div>
                    <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, margin: 0 }}>
                      Effectif vide. Tu ajouteras tes joueurs un à un.
                    </p>
                  </button>

                  <button
                    onClick={() => setRosterChoice('copy')}
                    disabled={previousPlayers.length === 0}
                    style={{
                      padding: '24px 20px',
                      background: previousPlayers.length === 0 ? '#f5f2eb' : T.surface,
                      border: `1.5px solid ${T.rule}`,
                      cursor: previousPlayers.length === 0 ? 'not-allowed' : 'pointer',
                      textAlign: 'left',
                      opacity: previousPlayers.length === 0 ? 0.5 : 1,
                      transition: 'border-color .15s',
                    }}
                    onMouseEnter={e => { if (previousPlayers.length) e.currentTarget.style.borderColor = T.gold }}
                    onMouseLeave={e => { if (previousPlayers.length) e.currentTarget.style.borderColor = T.rule }}
                  >
                    <div style={{ fontFamily: T.display, fontSize: 18, textTransform: 'uppercase', color: T.ink, marginBottom: 8 }}>
                      Reprendre l'effectif
                    </div>
                    <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, margin: 0 }}>
                      {previousPlayers.length} joueurs de la saison {currentSeason}. Tu ajustes ensuite.
                    </p>
                  </button>
                </div>
              )}

              {/* Mode fresh validé */}
              {rosterChoice === 'fresh' && (
                <div style={{ padding: '32px 20px', textAlign: 'center', background: T.surface, border: `1px solid ${T.rule}` }}>
                  <Check size={32} color={T.gold} style={{ margin: '0 auto 12px', display: 'block' }} />
                  <p style={{ fontSize: 14, color: T.ink, marginBottom: 8 }}>Tu repartiras avec un effectif vide.</p>
                  <p style={{ fontSize: 12, color: T.muted, marginBottom: 16 }}>Tu pourras ajouter tes joueurs depuis la page Effectif.</p>
                  <button
                    onClick={() => setRosterChoice(null)}
                    style={{ background: 'transparent', border: 'none', color: T.gold, fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Changer
                  </button>
                </div>
              )}

              {/* Mode copy : sélection des joueurs */}
              {rosterChoice === 'copy' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, margin: 0 }}>
                      {selectedPlayerIds.size} sur {previousPlayers.length} sélectionnés
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={bulkPromote ? resetBulk : applyBulkPromote}
                        style={{
                          background: bulkPromote ? T.gold : 'transparent',
                          border: `1px solid ${T.goldBdr}`,
                          color: bulkPromote ? T.ink : T.gold,
                          fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                          padding: '6px 10px', cursor: 'pointer',
                        }}
                      >
                        {bulkPromote ? '✓ Catégorie supérieure' : 'Tout passer en catégorie sup.'}
                      </button>
                      <button
                        onClick={() => setRosterChoice(null)}
                        style={{ background: 'transparent', border: 'none', color: T.muted, fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}
                      >
                        Changer
                      </button>
                    </div>
                  </div>

                  <div style={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${T.rule}` }}>
                    {previousPlayers.map(p => {
                      const checked = selectedPlayerIds.has(p.id)
                      const newCat = categoryAdjustments[p.id] || p.category
                      return (
                        <div
                          key={p.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 14px', borderBottom: `1px solid ${T.rule}`,
                            background: checked ? T.surface : '#fafaf7',
                            opacity: checked ? 1 : 0.55,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePlayer(p.id)}
                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: T.gold }}
                          />
                          <div style={{ flex: 1, fontSize: 13, color: T.ink }}>
                            <span style={{ fontWeight: 600 }}>{p.name}</span>
                            <span style={{ marginLeft: 8, color: T.muted, fontSize: 11 }}>
                              · {p.position} {p.number ? `· #${p.number}` : ''}
                            </span>
                          </div>
                          <select
                            value={newCat}
                            onChange={(e) => adjustCategory(p.id, e.target.value)}
                            disabled={!checked}
                            style={{
                              padding: '4px 8px',
                              fontFamily: T.mono, fontSize: 10, letterSpacing: '.04em',
                              background: newCat !== p.category ? T.goldBg : T.surface,
                              border: `1px solid ${newCat !== p.category ? T.gold : T.rule}`,
                              color: T.ink, cursor: checked ? 'pointer' : 'not-allowed',
                            }}
                          >
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer — boutons */}
        <div style={{ padding: '20px 28px', borderTop: `1px solid ${T.rule}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.surface }}>
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
              color: T.muted, padding: '8px 4px',
            }}
          >
            <ArrowLeft size={12} />
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>

          {step < 2 ? (
            <button
              onClick={() => {
                if (step === 1 && !canProceedStep1) return
                setStep(step + 1)
              }}
              disabled={loading || (step === 1 && !canProceedStep1)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 24px',
                background: T.gold, color: T.ink,
                border: 'none', cursor: 'pointer',
                fontFamily: T.mono, fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
                opacity: (step === 1 && !canProceedStep1) ? 0.4 : 1,
              }}
            >
              Continuer
              <ArrowRight size={12} />
            </button>
          ) : (
            <button
              onClick={finish}
              disabled={loading || rosterChoice === null}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 24px',
                background: T.gold, color: T.ink,
                border: 'none', cursor: 'pointer',
                fontFamily: T.mono, fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
                opacity: (loading || rosterChoice === null) ? 0.4 : 1,
              }}
            >
              {loading ? 'Préparation...' : `Préparer ${targetSeason}`}
              <Check size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
